import type { IServerSideDatasource, IServerSideGetRowsParams, IServerSideGetRowsRequest } from "ag-grid-enterprise";
import type { GridApi } from "~/components/grid/AgGrid";
import type { AppDispatch } from "~/store";

import { GROUP_AUTO_COLUMN_ID } from "ag-grid-enterprise";

import { type GridRequestInput } from "~/gql/graphqlBase.generated";
import recoverGridQueryRefusal from "~/helpers/grid/recoverGridQueryRefusal";
import { GRID_MUTATION_REFRESH_DELAY_MS } from "~/helpers/grid/refetchTagsAfterProjection";
import { registerServerGrid } from "~/helpers/grid/serverGridRegistry";
import { registerSsrmExportSource } from "~/helpers/grid/ssrmExportSource";
import logger from "~/helpers/logger";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { isCanceledGridRequestError, isRetryableError } from "~/services/graphqlErrorHelpers";
import { setGridTotalRows } from "~/store/agGridSlice";

export interface CreateSsrmDatasourceOptions<TRow = unknown, TServerRow = TRow> {
	getSearchText?: () => string | undefined;
	isReady?: () => boolean;
	onLoadFailure?: (retry: () => void, error: unknown) => void;
	onLoadSuccess?: (rows: TRow[], request: GridRequestInput) => void;
	onRowTotal?: (unfilteredRowCount: number) => void;
	onScopedRowCount?: (rowCount: number) => void;
	postProcessRows?: (rows: TServerRow[]) => TRow[] | Promise<TRow[]>;
	transformRequest?: (request: GridRequestInput) => GridRequestInput;
}

export const purgeServerSide = (api: GridApi | null | undefined): void => {
	api?.refreshServerSide({ purge: true });
};

export const refreshServerSideRows = (api: GridApi | null | undefined): void => {
	api?.refreshServerSide({ purge: false });
};

export const refreshServerSideAfterProjection = (api: GridApi | null | undefined): (() => void) => {
	const timeoutId = setTimeout(() => {
		if (api?.isDestroyed()) return;
		refreshServerSideRows(api);
	}, GRID_MUTATION_REFRESH_DELAY_MS);
	return () => clearTimeout(timeoutId);
};

export const refreshServerSideAfterMutation = (api: GridApi | null | undefined): (() => void) => {
	refreshServerSideRows(api);
	return refreshServerSideAfterProjection(api);
};

// baseApi already retries retryable errors once per request; these delays cover multi-second network blips.
export const RETRY_DELAYS_MS = [500, 1500];

export const waitFor = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// wsapi's agGridRows rejects the synthetic auto group column id; grouped-field sort entries carry the intent on their own.
const isSyntheticGroupColumn = (colId: string): boolean =>
	colId === GROUP_AUTO_COLUMN_ID || colId.startsWith(`${GROUP_AUTO_COLUMN_ID}-`);

export const stripSyntheticGroupSort = <T extends { colId: string }>(sortModel: T[]): T[] =>
	sortModel.filter((item) => !isSyntheticGroupColumn(item.colId));

export const buildGridRequest = (
	request: IServerSideGetRowsRequest,
	getSearchText?: () => string | undefined
): GridRequestInput => {
	const startRow = request.startRow ?? 0;
	const endRow = request.endRow ?? startRow;
	return {
		endRow,
		filterModel: request.filterModel ?? null,
		groupKeys: request.groupKeys.map(String),
		rowGroupCols: request.rowGroupCols.map((col) => ({ field: col.id })),
		searchText: getSearchText?.()?.trim() || undefined,
		sortModel: request.sortModel.map((item) => ({ colId: item.colId, sort: item.sort })),
		startRow,
		valueCols: request.valueCols.map((col) => ({ aggFunc: col.aggFunc ?? "", field: col.id }))
	};
};

export const buildFinalGridRequest = (
	request: IServerSideGetRowsRequest,
	getSearchText?: () => string | undefined,
	transformRequest?: (request: GridRequestInput) => GridRequestInput
): GridRequestInput => {
	const builtRequest = buildGridRequest(request, getSearchText);
	const transformedRequest = transformRequest ? transformRequest(builtRequest) : builtRequest;
	return { ...transformedRequest, sortModel: stripSyntheticGroupSort(transformedRequest.sortModel) };
};

export interface FetchAllRowsOptions<TRow = Record<string, unknown>> {
	abortRef: { current: boolean };
	batchSize?: number;
	dispatch: AppDispatch;
	getSearchText?: () => string | undefined;
	onBlock?: (blockRows: TRow[]) => void;
	onProgress?: (current: number, total: number) => void;
	stopIfTotalExceeds?: number;
	transformRequest?: (request: GridRequestInput) => GridRequestInput;
}

export interface FetchAllRowsResult<TRow = Record<string, unknown>> {
	cancelled: boolean;
	overLimit: boolean;
	rows: TRow[];
	totalCount: number;
}

// wsapi caps agGridRows blocks at MaxBlockSize (200); a wider endRow window is clamped server-side.
const SERVER_MAX_BLOCK_SIZE = 200;

export const fetchAllRows = async <TRow = Record<string, unknown>>(
	gridId: string,
	request: IServerSideGetRowsRequest,
	{
		abortRef,
		batchSize = SERVER_MAX_BLOCK_SIZE,
		dispatch,
		getSearchText,
		onBlock,
		onProgress,
		stopIfTotalExceeds,
		transformRequest
	}: FetchAllRowsOptions<TRow>
): Promise<FetchAllRowsResult<TRow>> => {
	const rows: TRow[] = [];
	let startRow = 0;
	let totalCount = 0;
	let overLimit = false;
	const pagePromises: Array<{ unsubscribe?: () => void }> = [];

	try {
		while (!abortRef.current) {
			const blockRequest: IServerSideGetRowsRequest = { ...request, endRow: startRow + batchSize, startRow };
			const gridRequest = buildFinalGridRequest(blockRequest, getSearchText, transformRequest);
			const promise = dispatch(
				apiEndpoints.endpoints.AgGridRows.initiate({ gridId, request: gridRequest }, { forceRefetch: true })
			);
			pagePromises.push(promise);
			const result = await promise;
			if (abortRef.current) break;

			if (result.error) throw new Error("Failed to fetch grid rows");
			const payload = result.data?.agGridRows;
			if (!payload) throw new Error("Failed to fetch grid rows");

			const blockRows = payload.rowData as TRow[];
			if (payload.rowCount != null) totalCount = payload.rowCount;
			rows.push(...blockRows);
			onBlock?.(blockRows);
			startRow += blockRows.length;
			onProgress?.(rows.length, totalCount || rows.length);

			if (stopIfTotalExceeds != null && totalCount > stopIfTotalExceeds) {
				overLimit = true;
				break;
			}

			if (blockRows.length < batchSize) break;
		}
		return { cancelled: abortRef.current, overLimit, rows, totalCount: totalCount || rows.length };
	} finally {
		for (const pagePromise of pagePromises) {
			pagePromise.unsubscribe?.();
		}
	}
};

// RTK Query auto-freezes cached rows (Immer) and AG Grid writes committed cell values onto the row object in place.
const toMutableRows = <TRow>(rows: TRow[]): TRow[] => rows.map((row) => ({ ...(row as object) }) as TRow);

// AG Grid runs several concurrent block requests by default, so supersession must key off the specific block.
export const buildBlockKey = (request: IServerSideGetRowsRequest, startRow: number, endRow: number): string =>
	JSON.stringify([request.groupKeys.map(String), startRow, endRow]);

const createSsrmDatasource = <TRow = unknown, TServerRow = TRow>(
	gridId: string,
	dispatch: AppDispatch,
	opts: CreateSsrmDatasourceOptions<TRow, TServerRow> = {}
): IServerSideDatasource => {
	const latestRequestIdByBlock = new Map<string, number>();
	let nextRequestId = 0;

	const getRows = async (params: IServerSideGetRowsParams): Promise<void> => {
		if (opts.isReady?.() === false) {
			params.success({ rowCount: 0, rowData: [] });
			return;
		}
		const { request } = params;
		if (params.api) registerServerGrid(params.api, gridId);
		const startRow = request.startRow ?? 0;
		const endRow = request.endRow ?? startRow;
		const blockSize = endRow - startRow;
		const blockKey = buildBlockKey(request, startRow, endRow);
		const requestId = ++nextRequestId;
		latestRequestIdByBlock.set(blockKey, requestId);
		const isSuperseded = (): boolean => latestRequestIdByBlock.get(blockKey) !== requestId;

		const gridRequest = buildFinalGridRequest(request, opts.getSearchText, opts.transformRequest);

		let failureError: unknown;

		for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
			if (isSuperseded()) return;

			const action = dispatch(
				apiEndpoints.endpoints.AgGridRows.initiate({ gridId, request: gridRequest }, { forceRefetch: true })
			);
			try {
				const result = await action;
				if (isSuperseded()) return;

				const payload = result.data?.agGridRows;
				if (payload) {
					const processed = opts.postProcessRows
						? await opts.postProcessRows(payload.rowData as TServerRow[])
						: (payload.rowData as TRow[]);
					if (isSuperseded()) return;
					const rowData = toMutableRows(processed);
					const lastRowReached = blockSize > 0 && rowData.length < blockSize;
					const rowCount = payload.rowCount ?? (lastRowReached ? startRow + rowData.length : undefined);
					params.success({ rowCount, rowData });
					opts.onLoadSuccess?.(rowData, gridRequest);

					if (payload.unfilteredRowCount != null) {
						dispatch(setGridTotalRows({ gridId, total: payload.unfilteredRowCount }));
						opts.onRowTotal?.(payload.unfilteredRowCount);
					}

					if (payload.rowCount != null) opts.onScopedRowCount?.(payload.rowCount);
					return;
				}
				failureError = result.error;
			} catch (error) {
				failureError = error;
			} finally {
				action.unsubscribe?.();
			}

			if (isSuperseded()) return;

			if (isCanceledGridRequestError(failureError)) {
				params.fail();
				return;
			}

			const delayMs = RETRY_DELAYS_MS[attempt];
			if (delayMs === undefined || !isRetryableError(failureError)) break;
			await waitFor(delayMs);
			if (params.api.isDestroyed()) return;
		}

		if (isSuperseded()) return;
		logger.error(`Grid rows request failed for ${gridId}`, failureError);
		params.fail();
		if (await recoverGridQueryRefusal(params.api, gridRequest, failureError)) return;
		opts.onLoadFailure?.(() => {
			if (!params.api.isDestroyed()) params.api.retryServerSideLoads();
		}, failureError);
	};

	const datasource: IServerSideDatasource = { getRows };
	registerSsrmExportSource(datasource, {
		getSearchText: opts.getSearchText,
		gridId,
		isReady: opts.isReady,
		postProcessRows: opts.postProcessRows,
		transformRequest: opts.transformRequest
	});
	return datasource;
};

export default createSsrmDatasource;
